import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysUser } from '../queryKeys';

import { createUsersViaCSV } from './createUsersViaCSV';
import {
  type CreateUsersViaCSVMutationOptions,
  type CreateUsersViaCSVPayload,
} from './types';

/**
 * Custom hook for creating users from a CSV file.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Admin/operation/create_users_csv_admin_users_csv_post
 */
export const useMutationCreateUsersViaCSV = (
  options?: CreateUsersViaCSVMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateUsersViaCSVPayload) =>
      createUsersViaCSV(payload),
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysUser.getAll({}),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
